import { createContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useLocation, useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { clearAxiosClientHeader, setAxiosClientHeader } from '../../helpers/AuthorizationHelpers'
import { getAdminUserClients, getUserAccessGroups } from '../../http'
import { AccessGroup } from '../../Http/DataTypes'
import { clearAxiosFeatureValueHeader, setAxiosFeatureValueHeader } from '../../Http/featureHttp'
import { Queries } from '../../queries'
import { IClient, IClientMinimal } from '../../types/data-types'
import { Spinner } from '../Common/Spinner'

interface IUserClientsContext {
  admin?: boolean
  setAdmin?: (admin: boolean) => any
  selectedClient?: IClient
  clients: IClient[]
  partnerClients: IClientMinimal[]
  setClient: (client: IClientMinimal) => any
  pushRoute: (route: string) => any
  selectedAccessGroup?: AccessGroup
  setSelectedAccessGroup?: (accessGroup: AccessGroup) => any
  clientAccessGroups?: AccessGroup[]
}

export const UserClientsContext = createContext<IUserClientsContext>(null)

function getClientSearchParams(client) {
  return new URLSearchParams({ client }).toString()
}

export const UserClientsContextProvider = ({ children }) => {
  const [searchParams] = useSearchParams()
  const [admin, setAdmin] = useState(true)
  const onClientsChange = (clients: IClient[]) => {
    if (!clients?.length) return setUserClient(null)
    const clientId = parseInt(searchParams.get('client'))
    if (!clientId) return setUserClient(clients[0])

    setUserClient(clients.find(c => c.clientId === clientId) ?? clients[0])
  }
  const { t } = useTranslation()
  const history = useNavigate()
  const location = useLocation()
  const allAccessGroups = { value: -2, title: t('dashboard:allAccessGroups') }
  const [clientAccessGroups, setClientAccessGroups] = useState([allAccessGroups])
  const { data: userClients, isLoading } = useQuery([Queries.USER_CLIENTS, admin], () => getAdminUserClients(admin), {
    onSuccess: onClientsChange,
  })

  useEffect(() => {
    return () => {
      clearAxiosClientHeader()
      clearAxiosFeatureValueHeader()
    }
  }, [])

  const [selectedClient, setClient] = useState<IClient>(userClients ? userClients[0] : null)

  useQuery(
    [Queries.CLIENT_ACCESS_GROUPS(selectedClient?.clientId)],
    () => getUserAccessGroups(selectedClient?.clientId),
    {
      onSuccess: data => {
        setClientAccessGroups([allAccessGroups, ...data])
      },
    },
  )
  const [selectedAccessGroup, setSelectedAccessGroup] = useState(allAccessGroups)
  useEffect(() => {
    if (userClients?.length > 0 && !selectedClient) setClient(userClients[0])
  }, [userClients])

  const setUserClient = (client: IClient) => {
    setAxiosClientHeader(client?.clientId)
    setAxiosFeatureValueHeader(client?.encryptedFeatureValue)
    setClient(client)
    setSelectedAccessGroup(allAccessGroups)
    if (!client) return
    const search = getClientSearchParams(client.clientId)
    history({ pathname: location.pathname, search })
  }

  const pushRoute = (pathname: string) => {
    const search = getClientSearchParams(selectedClient?.clientId)

    return history({ pathname, search })
  }

  const providerValue = {
    selectedClient,
    setClient: setUserClient,
    clients: Array.isArray(userClients) ? userClients.filter(c => !c.isPartner) : [],
    partnerClients: Array.isArray(userClients) ? userClients : [],
    selectedAccessGroup,
    setSelectedAccessGroup,
    clientAccessGroups,
    admin,
    setAdmin,
    pushRoute,
  }

  if (selectedClient) setAxiosClientHeader(selectedClient.clientId)

  return (
    <UserClientsContext.Provider value={providerValue}>
      {isLoading ? <Spinner /> : children}
    </UserClientsContext.Provider>
  )
}
